import React from 'react';
import { Flex, Text } from '@chakra-ui/react';
import {
  faChartLineUp,
  faFileCertificate
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge, ButtonText } from '@morressier/alembic';
import { ISubmissionFiguresAndTables } from '@publishing-engine/domain/models';
import { FormFieldFiguresAndTables } from '@publishing-engine/domain/types/article';
import { openFile } from 'src/utils/file';

export const FigureItem: React.FC<{
  item: ISubmissionFiguresAndTables[number];
  fieldConfig: Omit<FormFieldFiguresAndTables, 'component'>;
}> = ({ item, fieldConfig }) => (
  <Flex
    gap={4}
    padding={6}
    flexDirection={'column'}
    bgColor="surfacePrimaryFill"
    borderBottom="1px"
    borderColor="neutralPrimaryBorder"
    _last={{ borderBottom: 'none' }}
    width="100%"
  >
    <Flex gap={2} textStyle="headingItem" alignItems={'center'}>
      <Text key={item.referenceName}>{item.referenceName}</Text>
      <Text>
        {item.isReproduced ? (
          <Badge variant="attention" size="context">
            Reproduced
          </Badge>
        ) : (
          <Badge variant="neutral" size="context">
            Original
          </Badge>
        )}
      </Text>
    </Flex>
    {fieldConfig?.params.isSourceFileEnabled && (
      <Flex justifyContent="space-between">
        <Flex gap={2} flex="3" textStyle="textBody">
          <FontAwesomeIcon icon={faChartLineUp} fontSize={20} />
          <Text>Source File</Text>
        </Flex>
        <Flex flex="3">
          <Text>{item.sourceFile?.referenceName ?? 'Waiting for file'}</Text>
        </Flex>
        <ButtonText
          alignSelf="flex-start"
          disabled={!item.sourceFile?.url}
          onClick={() => item.sourceFile?.url && openFile(item.sourceFile?.url)}
        >
          View
        </ButtonText>
      </Flex>
    )}
    {item.isReproduced && fieldConfig?.params.isPermissionEvidenceEnabled && (
      <Flex justifyContent="space-between">
        <Flex gap={2} flex="3" textStyle="textBody">
          <FontAwesomeIcon icon={faFileCertificate} fontSize={20} />
          <Text>Proof of Permission</Text>
        </Flex>
        <Flex flex="3">
          <Text>
            {item.permissionEvidence?.referenceName ?? 'Waiting for file'}
          </Text>
        </Flex>
        <ButtonText
          alignSelf="flex-start"
          disabled={!item.permissionEvidence?.url}
          onClick={() =>
            item.permissionEvidence?.url &&
            openFile(item.permissionEvidence?.url)
          }
        >
          View
        </ButtonText>
      </Flex>
    )}
  </Flex>
);

interface Props {
  value: ISubmissionFiguresAndTables;
  fieldConfig: Omit<FormFieldFiguresAndTables, 'component'>;
}

export const FiguresAndTablesDisplay: React.FC<Props> = ({
  value,
  fieldConfig
}) => (
  <Flex
    flexDirection="column"
    justifyContent="space-between"
    bgColor="surfacePrimaryFill"
  >
    {value?.map(item => (
      <FigureItem
        key={item.referenceName}
        item={item}
        fieldConfig={fieldConfig}
      />
    ))}
  </Flex>
);
